import { doc, getDoc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

const SOUND_SRC = `${process.env.PUBLIC_URL || ''}/sugarcane-juice-sound-SnapYT.App.mp3`;
const SETTING_STORAGE_KEY = 'hrm_hr_login_sound_enabled';
const SETTINGS_COLLECTION = 'settings';
const SETTINGS_DOC_ID = 'audio_config';

let globalAudio = null;
let stopTimer = null;
let isAudioPlaying = false;

/**
 * Check if the given user is an HR member
 */
export const isUserHR = (user) => {
  if (!user) return false;
  const role = (user.role || '').toLowerCase().trim();
  const designation = (user.designation || '').toLowerCase().trim();
  const department = (user.department || '').toLowerCase().trim();

  return (
    role === 'hr' ||
    designation === 'hr' ||
    designation.includes('human resource') ||
    designation.includes('hr executive') ||
    designation.includes('hr manager') ||
    department === 'hr' ||
    department.includes('human resource')
  );
};

/**
 * Check synchronously whether HR login sound is enabled (localStorage fallback)
 * Default is true
 */
export const isHrLoginSoundEnabled = () => {
  try {
    const cached = localStorage.getItem(SETTING_STORAGE_KEY);
    if (cached === null) return true;
    return cached === 'true';
  } catch (e) {
    return true;
  }
};

/**
 * Fetch the latest setting from Firestore and cache it locally
 */
export const fetchHrLoginSoundSetting = async () => {
  try {
    const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const data = snap.data();
      const enabled = data.hrLoginSoundEnabled !== false;
      localStorage.setItem(SETTING_STORAGE_KEY, enabled ? 'true' : 'false');
      return enabled;
    } else {
      // Document does not exist yet; default to true
      localStorage.setItem(SETTING_STORAGE_KEY, 'true');
      return true;
    }
  } catch (err) {
    console.warn('[HRAudio] Could not fetch sound setting from Firestore:', err);
    return isHrLoginSoundEnabled();
  }
};

/**
 * Update the HR login sound setting in Firestore and localStorage
 */
export const updateHrLoginSoundSetting = async (enabled, userEmail = '') => {
  try {
    const boolValue = Boolean(enabled);
    localStorage.setItem(SETTING_STORAGE_KEY, boolValue ? 'true' : 'false');

    // Notify any local window listeners
    window.dispatchEvent(new CustomEvent('hr-sound-setting-changed', { detail: boolValue }));

    const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
    await setDoc(
      docRef,
      {
        hrLoginSoundEnabled: boolValue,
        updatedAt: serverTimestamp(),
        updatedBy: userEmail || 'admin'
      },
      { merge: true }
    );
    return true;
  } catch (err) {
    console.error('[HRAudio] Failed to update sound setting in Firestore:', err);
    return false;
  }
};

/**
 * Subscribe to real-time changes in Firestore
 */
export const subscribeHrLoginSoundSetting = (callback) => {
  try {
    const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const enabled = data.hrLoginSoundEnabled !== false;
          localStorage.setItem(SETTING_STORAGE_KEY, enabled ? 'true' : 'false');
          callback(enabled);
        } else {
          callback(isHrLoginSoundEnabled());
        }
      },
      (err) => {
        console.warn('[HRAudio] Subscription error:', err);
        callback(isHrLoginSoundEnabled());
      }
    );
    return unsubscribe;
  } catch (e) {
    console.warn('[HRAudio] Failed to establish subscription:', e);
    callback(isHrLoginSoundEnabled());
    return () => {};
  }
};

/**
 * Stop any playing HR sound immediately
 */
export const stopHrLoginSound = () => {
  if (stopTimer) {
    clearTimeout(stopTimer);
    stopTimer = null;
  }
  if (globalAudio) {
    try {
      globalAudio.pause();
      globalAudio.currentTime = 0;
    } catch (e) {}
    globalAudio = null;
  }
  isAudioPlaying = false;
};

/**
 * Play HR sound for durationSeconds (default 4 seconds)
 */
export const playHrLoginSound = (durationSeconds = 4) => {
  try {
    stopHrLoginSound();

    const audio = new Audio(SOUND_SRC);
    audio.preload = 'auto';
    audio.volume = 1.0;
    audio.currentTime = 0;
    globalAudio = audio;
    isAudioPlaying = true;

    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch((err) => {
        console.warn('[HRAudio] Autoplay blocked or playback error:', err);
        isAudioPlaying = false;
      });
    }

    // Stop after durationSeconds (4 seconds)
    stopTimer = setTimeout(() => {
      stopHrLoginSound();
    }, durationSeconds * 1000);

    return audio;
  } catch (err) {
    console.error('[HRAudio] Error playing sound:', err);
    isAudioPlaying = false;
    return null;
  }
};

export const isSoundCurrentlyPlaying = () => isAudioPlaying;
